import { Modal, Form, DatePicker, Typography, message } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import Label from 'common/components/Label';

import { useExtendTrialMutation } from './queries';

const { Text } = Typography;

const ExtendModal = ({ trial, onClose }) => {
  const [form] = Form.useForm();
  const extendTrialMutation = useExtendTrialMutation();

  const handleSubmit = values => {
    const payload = {
      eventTrialId: trial.id,
      expireAt: values.expireAt.format('YYYY-MM-DD'),
    };

    extendTrialMutation.mutate(payload, {
      onSuccess: () => {
        form.resetFields();
        onClose(true);
        message.success(t`Trial extended successfully`);
      },
      onError: () => {
        message.error(t`Error extending trial`);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  // Calculate initial expiry date (current date + remaining days)
  const initialExpiryDate = moment().add(trial.daysRemaining || 0, 'days');

  return (
    <Modal
      title={t`Extend Trial`}
      open
      onCancel={handleCancel}
      okButtonProps={{
        loading: extendTrialMutation.isLoading,
        htmlType: 'submit',
        form: 'extend-trial-form',
      }}
      confirmLoading={extendTrialMutation.isLoading}
      width={500}
      destroyOnClose
    >
      <Form
        form={form}
        id="extend-trial-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        preserve={false}
        initialValues={{
          expireAt: initialExpiryDate,
        }}
      >
        <Text>
          {t`Extend the expiry date for`} <strong>{trial.eventShortcode}</strong>.
        </Text>

        <Form.Item
          name="expireAt"
          label={<Label>{t`Trial Expiry Date`}</Label>}
          rules={[
            { required: true, message: t`Expiry Date is required` },
            {
              validator: (_, value) => {
                if (value && value.isBefore(moment(), 'day')) {
                  return Promise.reject(new Error(t`Expiry Date cannot be before today`));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                const maxExpiryDate = moment().add(1, 'year');
                if (value && value.isAfter(maxExpiryDate)) {
                  return Promise.reject(
                    new Error(t`Expiry Date cannot be longer than a year from now`),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          style={{ marginTop: 16 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={current => {
              const today = moment().startOf('day');
              const maxDate = moment().add(1, 'year');
              return current && (current < today || current > maxDate);
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

ExtendModal.propTypes = {
  trial: PropTypes.shape({
    id: PropTypes.string.isRequired,
    eventShortcode: PropTypes.string.isRequired,
    daysRemaining: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ExtendModal;
